import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { FuseSidebarService } from "@fuse/components/sidebar/sidebar.service";
import { FuseConfigService } from "@fuse/services/config.service";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { TranslateService } from "@ngx-translate/core";
import { UserModel } from "app/models/user.model";
import { AuthenticationService } from "app/services/authentication.service";
import { BrowserNotificationService } from "app/services/browserNotification.service";
import { BrowserNotificationMessage } from "app/services/browserNotification.service";
import * as _ from "lodash";
import { ToastrService } from "ngx-toastr";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ProjectService } from "app/services/api/project.service";
import { UserService } from "app/services/api/user.service";
import { ProjectModel } from "app/models/project.model";
import { ContextService } from "app/services/context.service";
import { environment as env } from "../../../../environments/environment";
import { BrowserNotificationDialogComponent } from "./browser-notification-dialog/browser-notification-dialog.component";
import { locale as catalan } from "./i18n/ca";
import { locale as english } from "./i18n/en";
import { locale as spanish } from "./i18n/es";
import { firstValueFrom } from "rxjs";

@Component({
    selector: "toolbar",
    templateUrl: "./toolbar.component.html",
    styleUrls: ["./toolbar.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class ToolbarComponent implements OnInit, OnDestroy {
    private static readonly browserNotificationPromptDismissedKey = "emma.browserNotifications.promptDismissed";
    private static readonly browserNotificationToastOptions = {
        timeOut: 8000,
        extendedTimeOut: 3000,
        easeTime: 200,
        closeButton: true,
        progressBar: true,
        tapToDismiss: false,
        enableHtml: true,
        titleClass: "emma-browser-toast__title",
        messageClass: "emma-browser-toast__message",
    };
    horizontalNavbar: boolean;
    rightNavbar: boolean;
    hiddenNavbar: boolean;
    languages: any;
    selectedLanguage: any;
    userStatusOptions: any[];
    user: UserModel;
    userProjects: ProjectModel[];
    private browserNotificationPromptShown = false;

    // Private
    private _unsubscribeAll: Subject<any>;

    /**
     * Constructor
     *
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {TranslateService} _translateService
     */
    constructor(
        private _fuseConfigService: FuseConfigService,
        private _fuseSidebarService: FuseSidebarService,
        private _translateService: TranslateService,
        private translationLoader: FuseTranslationLoaderService,
        private authenticationService: AuthenticationService,
        private browserNotificationService: BrowserNotificationService,
        private projectService: ProjectService,
        private userService: UserService,
        public sessionService: ContextService,
        private router: Router,
        private dialog: MatDialog,
        private toastr: ToastrService,
    ) {
        this.user = authenticationService.currentUser;
        this.translationLoader.loadTranslations(english, spanish, catalan);

        this.languages = [
            {
                id: "en",
                title: "English",
                flag: "us",
            },
            {
                id: "es",
                title: "Spanish",
                flag: "es",
            },
            {
                id: "ca",
                title: "Català",
                flag: "ca",
            },
        ];

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to the config changes
        this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((settings) => {
            this.horizontalNavbar = settings.layout.navbar.position === "top";
            this.rightNavbar = settings.layout.navbar.position === "right";
            this.hiddenNavbar = settings.layout.navbar.hidden === true;
        });

        // Set the selected language from default languages
        if (this.sessionService.preferredLanguage) {
            this._translateService.use(this.sessionService.preferredLanguage);
        }
        this.selectedLanguage = _.find(this.languages, { id: this._translateService.currentLang });

        // Update user projects if the user is correctly logged in
        if (this.user) {
            this.updateToolBarProjects();
            this.authenticationService.me().subscribe();
            void this.syncCurrentBrowserNotificationLanguage();
        }

        // Subscribe to changes in the user and update users projects accordingly
        this.authenticationService.onCurrentUserChange.subscribe((currentUser) => {
            this.user = currentUser;
            if (currentUser) {
                this.updateToolBarProjects();
                this.maybeOpenBrowserNotificationPrompt(currentUser);
                void this.syncCurrentBrowserNotificationLanguage();
            }
        });

        // In case of a forced reload, simply reload all the user data and the rest will
        // be handled by the already in place callbacks
        this.sessionService.onForceDataRefresh.subscribe(() => {
            this.authenticationService.me().subscribe();
        });

        this.browserNotificationService.messages$.pipe(takeUntil(this._unsubscribeAll)).subscribe((message) => {
            this.showBrowserNotificationToast(message);
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

    /**
     * Search
     *
     * @param value
     */
    search(_value): void {
        // Intentionally left blank. Toolbar search is not implemented here.
    }

    /**
     * Set the language
     *
     * @param lang
     */
    setLanguage(lang): void {
        // Set the selected language for the toolbar
        this.selectedLanguage = lang;

        // Use the selected language for translations
        this._translateService.use(lang.id);

        // Save as preference
        this.sessionService.preferredLanguage = lang.id;

        void this.syncBrowserNotificationLanguage(lang.id);
    }

    logout() {
        this.authenticationService.logout();
        this.router.navigate(["/login"]);
    }

    openHelp(): void {
        const language = this.selectedLanguage?.id || "en";
        const url = `${env.helpUrl.replace(/\/$/, "")}/${language}/`;

        window.open(url, "_blank", "noopener,noreferrer");
    }

    selectedProject() {
        // force content re-rendering
    }

    openBrowserNotifications(markPromptDismissedOnClose = false) {
        const currentProjectId = this.sessionService.currentProject;
        const currentProject = this.userProjects?.find((project) => project.id === currentProjectId);

        const dialogRef = this.dialog.open(BrowserNotificationDialogComponent, {
            width: "420px",
            maxWidth: "95vw",
            data: {
                publicKey: env.webPush?.publicKey || "",
                projectId: currentProjectId,
                projectName: currentProject?.name || "",
            },
        });

        dialogRef.afterClosed().subscribe((enabled) => {
            if (enabled === true) {
                this.clearBrowserNotificationPromptDismissed();
                return;
            }

            if (markPromptDismissedOnClose) {
                this.setBrowserNotificationPromptDismissed();
            }
        });
    }

    get browserNotificationsEnabledForThisBrowser(): boolean {
        return this.user?.browserNotificationsEnabled === true && this.browserNotificationService.hasGrantedPermission();
    }

    get browserNotificationsDisabledForCurrentProject(): boolean {
        const currentProjectId = this.sessionService.currentProject;
        if (!currentProjectId) {
            return false;
        }

        return this.user?.browserNotificationDisabledProjectIds?.includes(currentProjectId) ?? false;
    }

    get browserNotificationIcon(): string {
        if (!this.browserNotificationsEnabledForThisBrowser) {
            return "notifications_none";
        }

        if (this.browserNotificationsDisabledForCurrentProject) {
            return "notifications_off";
        }

        return "notifications_active";
    }

    get showBrowserNotificationAttention(): boolean {
        return (
            Boolean(env.webPush?.publicKey) &&
            this.browserNotificationService.isSupported() &&
            !this.browserNotificationsEnabledForThisBrowser
        );
    }

    private maybeOpenBrowserNotificationPrompt(user: UserModel) {
        const permissionState = this.browserNotificationService.getPermissionState();
        const notificationsEnabledForThisBrowser = user?.browserNotificationsEnabled === true && permissionState === "granted";

        if (
            this.browserNotificationPromptShown ||
            !user ||
            !env.webPush?.publicKey ||
            !this.browserNotificationService.isSupported() ||
            notificationsEnabledForThisBrowser ||
            this.isBrowserNotificationPromptDismissed()
        ) {
            return;
        }

        this.browserNotificationPromptShown = true;
        this.openBrowserNotifications(true);
    }

    private getBrowserNotificationPromptDismissedKey(): string | null {
        if (!this.user?.id) {
            return null;
        }

        return `${ToolbarComponent.browserNotificationPromptDismissedKey}.${this.user.id}`;
    }

    private isBrowserNotificationPromptDismissed(): boolean {
        if (typeof localStorage === "undefined") {
            return false;
        }

        const key = this.getBrowserNotificationPromptDismissedKey();
        if (!key) {
            return false;
        }

        return localStorage.getItem(key) === "true";
    }

    private setBrowserNotificationPromptDismissed(): void {
        if (typeof localStorage === "undefined") {
            return;
        }

        const key = this.getBrowserNotificationPromptDismissedKey();
        if (!key) {
            return;
        }

        localStorage.setItem(key, "true");
    }

    private clearBrowserNotificationPromptDismissed(): void {
        if (typeof localStorage === "undefined") {
            return;
        }

        const key = this.getBrowserNotificationPromptDismissedKey();
        if (!key) {
            return;
        }

        localStorage.removeItem(key);
    }

    private showBrowserNotificationToast(message: BrowserNotificationMessage): void {
        const title = message.title || "EmmaDB";
        const severity = this.getBrowserNotificationSeverity(message);
        const toastClass = severity ? `ngx-toastr emma-browser-toast emma-browser-toast--${severity}` : "ngx-toastr emma-browser-toast";
        const content = this.buildBrowserNotificationToastContent(message, title, severity);

        this.toastr.info(content.message, content.title, {
            ...ToolbarComponent.browserNotificationToastOptions,
            toastClass,
        });
    }

    private getBrowserNotificationSeverity(message: BrowserNotificationMessage): "alarm" | "warning" | "success" | null {
        switch (message.severity) {
            case "alarm":
            case "warning":
            case "success":
                return message.severity;
            default:
                return null;
        }
    }

    private buildBrowserNotificationToastContent(
        message: BrowserNotificationMessage,
        title: string,
        severity: "alarm" | "warning" | "success" | null,
    ): { title: string; message: string } {
        const details = this.extractBrowserNotificationDetails(message, title);

        return {
            title: "",
            message: [
                `<div class="emma-browser-toast__header">`,
                `<div class="emma-browser-toast__icon${severity ? ` emma-browser-toast__icon--${severity}` : ""}"><span class="material-icons">${this.getBrowserNotificationSeverityIcon(severity)}</span></div>`,
                `<div class="emma-browser-toast__header-copy">`,
                `<div class="emma-browser-toast__headline">${this.escapeBrowserNotificationHtml(this.getBrowserNotificationHeadline(severity))}</div>`,
                `<div class="emma-browser-toast__timestamp">${this.escapeBrowserNotificationHtml(details.timestamp)}</div>`,
                `</div>`,
                `</div>`,
                `<div class="emma-browser-toast__grid">`,
                this.buildBrowserNotificationField(this._translateService.instant("TOOLBAR.BrowserNotificationFieldAlarm"), details.alarm),
                this.buildBrowserNotificationField(
                    this._translateService.instant("TOOLBAR.BrowserNotificationFieldProject"),
                    details.project,
                ),
                this.buildBrowserNotificationField(
                    this._translateService.instant("TOOLBAR.BrowserNotificationFieldDevice"),
                    details.device,
                ),
                `</div>`,
            ].join(""),
        };
    }

    private buildBrowserNotificationField(label: string, value: string): string {
        return [
            `<div class="emma-browser-toast__field">`,
            `<div class="emma-browser-toast__field-label">${this.escapeBrowserNotificationHtml(label)}</div>`,
            `<div class="emma-browser-toast__field-value">${this.escapeBrowserNotificationHtml(value)}</div>`,
            `</div>`,
        ].join("");
    }

    private extractBrowserNotificationDetails(
        message: BrowserNotificationMessage,
        title: string,
    ): {
        timestamp: string;
        project: string;
        alarm: string;
        device: string;
    } {
        if (message.project || message.alarm || message.device || message.timestamp) {
            return {
                timestamp: this.formatBrowserNotificationTimestamp(message.timestamp || ""),
                project: message.project || "EmmaDB",
                alarm: message.alarm || title,
                device: message.device || "",
            };
        }

        const lines = (message.body || "")
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line.length > 0);
        const timestamp = this.formatBrowserNotificationTimestamp(lines.at(-1) || "");
        const summary = lines.slice(0, -1).join(" ");
        const titleMatch = title.match(/^emmaDB (?:Alarm|Alarma|Warning|Advertencia|Avís) \[(.+?)\/(.+?)\]$/i);
        const bodyMatch = summary.match(
            /^emmaDB:\s+(.+?)\s+-\s+(?:Alarm|Alarma|Warning|Advertencia|Avís)\s+"(.+?)"\s+.+?\s+(?:for device|para el dispositivo|per al dispositiu)\s+(.+)$/i,
        );

        return {
            timestamp,
            project: bodyMatch?.[1] || titleMatch?.[1] || "EmmaDB",
            alarm: bodyMatch?.[2] || titleMatch?.[2] || title,
            device: bodyMatch?.[3] || "",
        };
    }

    private formatBrowserNotificationTimestamp(value: string): string {
        if (!value) {
            return "";
        }

        const timestamp = new Date(value);
        if (Number.isNaN(timestamp.getTime())) {
            return value;
        }

        return this.formatBrowserNotificationDate(timestamp);
    }

    private getBrowserNotificationLocale(): string {
        switch (this.sessionService.preferredLanguage || this._translateService.currentLang) {
            case "es":
                return "es";
            case "ca":
                return "ca";
            default:
                return "en-US";
        }
    }

    private formatBrowserNotificationDate(timestamp: Date): string {
        const locale = this.getBrowserNotificationLocale();
        const options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
        };

        try {
            return new Intl.DateTimeFormat(locale, options).format(timestamp);
        } catch (_error) {
            return new Intl.DateTimeFormat("en-US", options).format(timestamp);
        }
    }

    private getBrowserNotificationHeadline(severity: "alarm" | "warning" | "success" | null): string {
        switch (severity) {
            case "warning":
                return this._translateService.instant("TOOLBAR.BrowserNotificationHeadlineWarning");
            case "alarm":
                return this._translateService.instant("TOOLBAR.BrowserNotificationHeadlineAlarm");
            case "success":
                return this._translateService.instant("TOOLBAR.BrowserNotificationHeadlineSuccess");
            default:
                return this._translateService.instant("TOOLBAR.BrowserNotificationHeadlineInfo");
        }
    }

    private getBrowserNotificationSeverityIcon(severity: "alarm" | "warning" | "success" | null): string {
        switch (severity) {
            case "alarm":
                return "warning_amber";
            case "warning":
                return "error_outline";
            case "success":
                return "check_circle";
            default:
                return "notifications";
        }
    }

    private escapeBrowserNotificationHtml(value: string): string {
        return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    }

    private async syncBrowserNotificationLanguage(languageId: string): Promise<void> {
        if (
            !languageId ||
            !env.webPush?.publicKey ||
            !this.user?.browserNotificationsEnabled ||
            !this.browserNotificationService.hasGrantedPermission()
        ) {
            return;
        }

        try {
            const subscription = await this.browserNotificationService.subscribe(env.webPush.publicKey);
            const normalizedLanguage = languageId.trim().toLowerCase();
            const existingSubscription = this.user?.browserNotificationSubscriptions?.find(
                (candidate) => candidate.endpoint === subscription.endpoint,
            );

            if (existingSubscription?.language === normalizedLanguage) {
                return;
            }

            subscription.language = normalizedLanguage;

            const updatedUser = await firstValueFrom(this.userService.subscribeBrowserNotifications(subscription));

            if (updatedUser) {
                this.user = updatedUser;
                this.authenticationService.setCurrentUser(updatedUser);
            }
        } catch (error) {
            console.error("Could not sync browser notification language", error);
        }
    }

    private async syncCurrentBrowserNotificationLanguage(): Promise<void> {
        const languageId = this.sessionService.preferredLanguage || this._translateService.currentLang;

        if (!languageId) {
            return;
        }

        await this.syncBrowserNotificationLanguage(languageId);
    }

    private updateToolBarProjects() {
        const currentProject = this.sessionService.currentProject;

        if (this.user.isAdmin) {
            this.projectService.getAll().subscribe((projects) => {
                this.userProjects = projects.filter((p) => !p.archived);
                this.ensureCurrentProjectSelection(currentProject);
            });
        } else {
            this.userProjects = this.user.projectsData;
            this.ensureCurrentProjectSelection(currentProject);
        }
    }

    private ensureCurrentProjectSelection(currentProject: string) {
        if (!this.userProjects?.length) {
            this.sessionService.currentProject = "";
            return;
        }

        // If no project is selected, or current selection does not exist, just choose the first one.
        if (!currentProject || !this.userProjects.some((p) => p.id === currentProject)) {
            this.sessionService.currentProject = this.userProjects[0].id;
        }
    }
}
